import { ViewStatus } from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { InfiniteData, QueryClient } from 'react-query';
import { VoicemailQueryData, VoicemailTableDataRow } from '../components/all-calls/types';
import { mutateVoicemailData } from '../components/voicemails/voicemail-update';

export const rebuildPagesFromRows = (
  allRows: VoicemailTableDataRow[],
  pageSize: number,
  originalPages: VoicemailQueryData[]
): VoicemailQueryData[] => {
  const newAllData = [];

  // Split the data into chunks of pageSize
  for (let i = 0; i < allRows.length; i += pageSize) {
    const chunk = allRows.slice(i, i + pageSize);
    newAllData.push(chunk);
  }

  // Build the page objects from the new page data chunks
  return newAllData.map((slice, i, list) => {
    const thereUsedToBeMoreRows = i === list.length - 1 && originalPages.length === list.length + 1;
    const hasNext = thereUsedToBeMoreRows ? false : originalPages[i]?.meta?.hasNext;

    return {
      rows: slice,
      meta: {
        lastId: slice[slice.length - 1]?.voicemailId,
        createdAt: slice[slice.length - 1]?.createdAt,
        hasNext,
      },
    };
  });
};

export const shouldPrefetchNextPage = (
  pages: VoicemailQueryData[],
  pageNumber: number,
  targetPageSlice: VoicemailQueryData
): boolean => {
  const pageIndexWithDeletedData = pageNumber - 1;
  return pages.length === pageIndexWithDeletedData + 1 && targetPageSlice.meta.hasNext;
};

export type OptimisticDeleteOptions = {
  queryClient: QueryClient;
  fullQueryKey: (string | boolean | number)[];
  pageSize: number;
  pageNumber: number;
  index: number;
  fetchNextPage?: (options: any) => Promise<any>;
};

export const performOptimisticDelete = async (
  options: OptimisticDeleteOptions
): Promise<InfiniteData<VoicemailQueryData> | null> => {
  const { queryClient, fullQueryKey, pageSize, pageNumber, index, fetchNextPage } = options;
  const data = queryClient.getQueryData<InfiniteData<VoicemailQueryData>>(fullQueryKey);

  if (!data || !fetchNextPage) return null;

  const pageIndexWithDeletedData = pageNumber - 1;
  const targetPageSlice = data.pages[pageIndexWithDeletedData];
  const needToPrefetch = shouldPrefetchNextPage(data.pages, pageNumber, targetPageSlice);

  // Get reference data (potentially with next page prefetched)
  const refData = needToPrefetch
    ? await fetchNextPage({
        pageParam: {
          lastId: targetPageSlice.meta.lastId,
          createdAt: targetPageSlice.meta.createdAt,
        },
      }).then((res) => res.data ?? { pages: [], pageParams: [] })
    : data;

  // Remove the deleted item from all rows
  const overallDeleteIndex = pageSize * (pageNumber - 1) + index;
  const allData = refData.pages.map((page: any) => page.rows).flat();
  allData.splice(overallDeleteIndex, 1);

  // Rebuild pages from the updated rows
  const newPages = rebuildPagesFromRows(allData, pageSize, data.pages);

  return {
    pages: newPages,
    pageParams: newPages.map((page, i) => (i === 0 ? undefined : page.meta)),
  };
};

export type OptimisticUpdateOptions = {
  queryClient: QueryClient;
  fullQueryKey: (string | boolean | number)[];
  voicemailId: string;
  pageSize: number;
  updateFn: (row: VoicemailTableDataRow) => void;
};

export const performOptimisticUpdate = (options: OptimisticUpdateOptions): InfiniteData<VoicemailQueryData> | null => {
  const { queryClient, fullQueryKey, voicemailId, pageSize, updateFn } = options;
  const data = queryClient.getQueryData<InfiniteData<VoicemailQueryData>>(fullQueryKey);

  if (!data) return null;

  return (
    mutateVoicemailData({
      pageSize,
      voicemailId,
      voicemailData: data,
      callbackFn: (mutationData) => {
        if (mutationData.voicemailRow) {
          updateFn(mutationData.voicemailRow);
        }
      },
    }) || null
  );
};

export const createReadStatusUpdater = (status: ViewStatus) => (row: VoicemailTableDataRow) => {
  row.readAt = status === ViewStatus.VIEW_STATUS_READ ? new Date().toISOString() : undefined;
};
